import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, from, map, switchMap, take, catchError, timer, filter, timeout, Observable, TimeoutError } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { ReturnPathHelper } from '../../services/authentication/return-path-helper';
import { RoutingService } from '../../services/routing/routing.service';
import { AfRoutes } from '../../routes';

interface OidcErrorAttributes {
  error: string;
  errorDescription?: string;
  errorUri?: string;
  state?: string;
}

@Component({
  selector: 'af-auth-callback',
  imports: [CommonModule, ButtonsModule, LoaderModule],
  templateUrl: './auth-callback.component.html',
  styleUrl: './auth-callback.component.scss',
})
export class AuthCallbackComponent implements OnInit {

  error?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private routingService: RoutingService,
  ) {}

  public async goToLogin(): Promise<void> {
    const didNavigate = await this.router.navigate([AfRoutes.login], { replaceUrl: true });
    if (!didNavigate) {
      throw new Error('Could not navigate to sign in.');
    }
  }

  ngOnInit(): void {
    this.authenticationService
      .isValidSession()
      .pipe(
        take(1),
        switchMap((hasValidSession) => hasValidSession
          ? this.completeAuthentication()
          : this.handleMissingAuthSession()
        ),
        catchError((err) => {
          if (err instanceof TimeoutError) {
            this.error = 'Sign-in timed out. Please try again.';
          } else {
            const detail = err?.message || 'Unknown error occurred.';
            this.error = `Authentication failed. ${detail}`;
          }

          return EMPTY;
        })
      )
      .subscribe();
  }

  private handleMissingAuthSession(): Observable<void> {
    const oidcErrorAttributes = this.getOidcErrorAttributes();
    if (oidcErrorAttributes) {
      throw new Error(oidcErrorAttributes.errorDescription ?? oidcErrorAttributes.error);
    }

    return this.waitForValidSession().pipe(
      switchMap(() => this.completeAuthentication())
    );
  }

  private getOidcErrorAttributes(): OidcErrorAttributes | undefined {
    const queryParamMap = this.route.snapshot.queryParamMap;
    const error = queryParamMap.get('error');
    if (!error) {
      return undefined;
    }

    const errorDescription = queryParamMap.get('error_description');
    const errorUri = queryParamMap.get('error_uri');
    const state = queryParamMap.get('state');

    return {
      error,
      ...(errorDescription ? { errorDescription } : {}),
      ...(errorUri ? { errorUri } : {}),
      ...(state ? { state } : {}),
    };
  }

  private completeAuthentication(): Observable<void> {
    const queryParamMap = this.route.snapshot.queryParamMap;
    const state = queryParamMap.get('state');

    return this.authenticationService.refreshAuthStateFromSession().pipe(
      switchMap(() => this.authorizationService.updateRoles()),
      switchMap(() => this.navigateToPostLoginPage(state))
    );
  }

  private waitForValidSession(): Observable<void> {
    return timer(0, 250).pipe(
      switchMap(() => this.authenticationService.isValidSession().pipe(take(1))),
      filter((hasValidSession) => hasValidSession),
      take(1),
      timeout({ first: 10000 }), // 10 sec for session to become valid
      map(() => undefined)
    );
  }

  private extractReturnPathRouteFromState(state: string | null): AfRoutes | undefined {
    const customState = ReturnPathHelper.extractReturnPathFromState(state);

    // Return matching route or undefined
    return Object.values(AfRoutes).find((v) => v === customState);
  }

  private navigateToPostLoginPage(state: string | null): Observable<void> {
    const returnPathRoute = this.extractReturnPathRouteFromState(state);

    const targetRoute$ = returnPathRoute
      ? from(Promise.resolve(returnPathRoute))
      : this.routingService.getPostLoginRoute();

    return targetRoute$.pipe(
      switchMap((targetRoute) => from(this.router.navigate([targetRoute]))),
      map((didNavigate) => {
        if (!didNavigate) {
          throw new Error('Could not navigate to post-login page.');
        }
      })
    );
  }
}
